import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import { productAPI, BASE_URL } from "../../services/api";

const getBrandLogo = (brand) => {
  if (!brand.logo_url) return null;
  if (brand.logo_url.startsWith("http")) return brand.logo_url;
  return `${BASE_URL}${brand.logo_url}`;
};

const BrandsSection = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productAPI.getGlobalBrands()
      .then(res => setBrands((res.data?.data || []).filter(b => b.is_active)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="py-14 bg-white border-t border-primary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-6 w-32 bg-primary-100 animate-pulse rounded mb-6" />
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="animate-pulse bg-primary-50 rounded-2xl h-24" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (brands.length === 0) return null;

  return (
    <section className="py-14 bg-white border-t border-primary-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-1 bg-secondary-600 rounded-full" />
              <span className="text-xs font-bold uppercase tracking-widest text-secondary-600">Trusted Partners</span>
            </div>
            <h2 className="text-3xl font-extrabold text-primary-800">
              Top <span className="text-secondary-600">Brands</span>
            </h2>
          </div>
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-3">
          {brands.map(brand => {
            const logo = getBrandLogo(brand);
            return (
              <Link
                key={brand.id}
                to={`/brands/${brand.id}`}
                className="group flex flex-col items-center bg-primary-50 border border-primary-100 rounded-2xl p-4 hover:border-secondary-300 hover:bg-secondary-50 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-white border border-primary-100 flex items-center justify-center mb-2.5 shadow-sm group-hover:shadow-md transition-shadow">
                  {logo ? (
                    <img
                      src={logo}
                      alt={brand.name}
                      className="w-full h-full object-contain p-1"
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <Building2 className={`w-6 h-6 text-primary-300 ${logo ? 'hidden' : 'flex'}`} style={{ display: logo ? 'none' : 'flex' }} />
                </div>
                <p className="text-[11px] font-bold text-primary-700 group-hover:text-secondary-700 text-center uppercase tracking-wide leading-tight transition-colors line-clamp-2">
                  {brand.name}
                </p>
                <span className="mt-1.5 text-[10px] font-semibold text-secondary-600 opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                  View Products <ArrowRight className="w-2.5 h-2.5" />
                </span>
              </Link>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default BrandsSection;
